import {
  AttributeValue,
  DynamoDB,
  PutItemInput,
} from "@aws-sdk/client-dynamodb";
import {
  GetCommandOutput,
  PutCommandInput,
  QueryCommandInput,
  ScanCommandInput,
  UpdateCommandInput,
} from "@aws-sdk/lib-dynamodb";
import Bluebird from "bluebird";
import { chunk } from "lodash";

import { delay, getTableNameInLambdaEnvironment } from "@examspark/common";
import { DocumentClient, DocumentKey } from "@examspark/common-aws";
import {
  AnyObject,
  AnyResource,
  IDSK,
  Objective,
  Replace,
  Resource,
  Tag,
  TagInput,
  Workspace,
} from "@examspark/types";

export * from "./_common";
export * from "./app";
export * from "./app-domain";
export * from "./bank";
export * from "./classroom";
export * from "./content";
export * from "./course";
export * from "./exam";
export * from "./examConfig";
export * from "./examSession";
export * from "./issue";
export * from "./item";
export * from "./objective";
export * from "./outlets";
export * from "./relationships";
export * from "./relatives";
export * from "./restapi";
export * from "./role";
export * from "./shareUtils";
export * from "./signing";
export * from "./unit";
export * from "./user";
export * from "./workspace";

const ddbSharedClient = new DynamoDB();
const ddbSharedDocumentClient = new DocumentClient(ddbSharedClient);

export const getItem = async <T extends AnyObject = AnyObject>(
  ddb: DocumentClient,
  TableName: string,
  Key: DocumentKey,
): Promise<Replace<GetCommandOutput, { Item: T | undefined }>> => {
  return (await ddb.get({
    TableName,
    Key,
  })) as Replace<GetCommandOutput, { Item: T | undefined }>;
};

export const putItem = async <T extends AnyObject>(
  ddb: DocumentClient,
  TableName: string,
  Item: T,
  additionalPutParams?: PutItemInput,
) => {
  return await ddb.put({
    ...additionalPutParams,
    TableName,
    Item,
  });
};

export const updateItem = async (
  ddb: DocumentClient,
  input: UpdateCommandInput,
) => {
  return await ddb.update(input);
};

export const replaceItem = async (
  ddb: DocumentClient,
  tableName: string,
  key: IDSK,
  next: any,
) => {
  const promises: any[] = [];
  promises.push(deleteItem(ddb, tableName, key));
  promises.push(putItem(ddb, tableName, next));
  return Promise.all(promises);
};

export async function batchGetOrdered<
  T extends Record<string, any> = Record<string, any>,
>(
  ddb: DocumentClient,
  TableName: string,
  Keys: DocumentKey[],
): Promise<Array<T | undefined>> {
  const items = await batchGet(ddb, TableName, Keys);
  return Keys.map((k) => items.find((i) => keyEquals(k, i))) as Array<
    T | undefined
  >;
}

export async function batchGet<T extends Record<string, any>>(
  ddb: DocumentClient,
  TableName: string,
  Keys: DocumentKey[],
): Promise<Array<T>> {
  if (Keys.length === 0) return [];

  const chunks = chunk(Keys, 100);
  const items: Array<T> = [];

  for (const chunk of chunks) {
    const result = await ddb.batchGet({
      RequestItems: {
        [TableName]: {
          Keys: chunk,
        },
      },
    });
    const _items = result.Responses?.[TableName];
    if (_items) {
      items.push(...(_items as T[]));
    }
  }

  return items;
}

export const queryAll = async <T = IDSK>(
  ddb: DocumentClient,
  query: QueryCommandInput,
  params?: {
    limit?: number;
    current?: number;
    ExclusiveStartKey?: DocumentKey;
  },
) => {
  let items: T[] = [];
  const result = await ddb.query({
    ...query,
    ExclusiveStartKey: params?.ExclusiveStartKey,
  });
  items = items.concat(result.Items as T[]);
  // console.log(`Querying ${JSON.stringify(params?.ExclusiveStartKey)}`);

  if (
    result.LastEvaluatedKey &&
    (!params?.limit ||
      (params.limit && (params.current || 0) < (params.limit || 1)))
  ) {
    items = items.concat(
      await queryAll(ddb, query, {
        current: (params?.current || 0) + 1,
        limit: params?.limit,
        ExclusiveStartKey: result.LastEvaluatedKey,
      }),
    );
  }

  return items;
};

export const getQueryCommandInput = (
  params: {
    tableName: string;
  } & (
    | {
        indexName: "byWorkspaceId";
        workspaceId: string;
        sortKeyBeginsWith?: string;
        sortKeyEquals?: string;
      }
    | {
        indexName: "bySortKey";
        sk: string;
      }
  ),
) => {
  const { tableName: TableName } = params;

  const queryInput: QueryCommandInput = {
    TableName,
    IndexName: params.indexName,
    KeyConditionExpression: "",
  };

  if (params.indexName === "byWorkspaceId") {
    queryInput.KeyConditionExpression += "workspaceId = :workspaceId";
    queryInput.ExpressionAttributeValues = {
      ":workspaceId": params.workspaceId,
    };

    if (params.sortKeyEquals) {
      queryInput.KeyConditionExpression += ` AND sk = :sk`;
      queryInput.ExpressionAttributeValues = {
        ...queryInput.ExpressionAttributeValues,
        ":sk": params.sortKeyEquals,
      };
    } else if (params.sortKeyBeginsWith) {
      queryInput.KeyConditionExpression += ` AND begins_with(sk, :sk)`;
      queryInput.ExpressionAttributeValues = {
        ...queryInput.ExpressionAttributeValues,
        ":sk": params.sortKeyBeginsWith,
      };
    }
  } else if (params.indexName === "bySortKey") {
    queryInput.KeyConditionExpression += "sk = :sk";
    queryInput.ExpressionAttributeValues = {
      ":sk": params.sk,
    };
  }

  // console.log(queryInput);
  return queryInput;
};

export const queryAllResourcesById = async <T = IDSK>(
  ddb: DocumentClient,
  params: {
    tableName: string;
    id: string;
    sortKeyBeginsWith?: string;
    sortKeyEquals?: string;
  },
) => {
  const query: QueryCommandInput = {
    TableName: params.tableName,
    KeyConditionExpression: "id = :id",
    ExpressionAttributeValues: {
      ":id": params.id,
    },
  };

  if (params.sortKeyEquals) {
    query.KeyConditionExpression += ` AND sk = :sk`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":sk": params.sortKeyEquals,
    };
  } else if (params.sortKeyBeginsWith) {
    query.KeyConditionExpression += ` AND begins_with(sk, :sk)`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":sk": params.sortKeyBeginsWith,
    };
  }

  const result = await queryAll<T>(ddb, query);
  return result;
};

export const queryResourcesByWorkspaceId = (params: {
  TableName: string;
  ExclusiveStartKey?: DocumentKey;
  workspaceId: string;
  sortKeyExpression?: "beginsWith" | "equals";
  sortKeyValue: string;
}) => {
  const { TableName, ExclusiveStartKey } = params;

  let queryExpression = "#hk = :hkv";
  switch (params.sortKeyExpression) {
    case "beginsWith":
      queryExpression += " AND begins_with(#sk, :skv)";
      break;
    case "equals":
      queryExpression += " AND #sk = :skv";
      break;
  }

  const queryInput: QueryCommandInput = {
    TableName,
    IndexName: "byWorkspaceId",
    ExclusiveStartKey,
    KeyConditionExpression: queryExpression,
    ExpressionAttributeNames: {
      "#hk": "workspaceId",
    },
    ExpressionAttributeValues: {
      ":hkv": params.workspaceId,
    },
  };

  if (
    queryInput.ExpressionAttributeNames &&
    queryInput.ExpressionAttributeValues
  ) {
    queryInput.ExpressionAttributeNames["#sk"] = "sk";
    queryInput.ExpressionAttributeValues[":skv"] = params.sortKeyValue;
  }

  return queryInput;
};

export const queryAllResourcesByWorkspaceId = async <T = IDSK>(
  ddb: DocumentClient,
  params: {
    tableName: string;
    workspaceId: string;
    sortKeyBeginsWith?: string;
    sortKeyEquals?: string;
    limit?: number;
    direction?: "asc" | "desc";
  },
) => {
  const query: QueryCommandInput = {
    TableName: params.tableName,
    IndexName: "byWorkspaceId",
    KeyConditionExpression: "workspaceId = :workspaceId",
    ExpressionAttributeValues: {
      ":workspaceId": params.workspaceId,
    },
    ScanIndexForward: params.direction === "desc",
  };

  if (params.sortKeyEquals) {
    query.KeyConditionExpression += ` AND sk = :sk`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":sk": params.sortKeyEquals,
    };
  } else if (params.sortKeyBeginsWith) {
    query.KeyConditionExpression += ` AND begins_with(sk, :sk)`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":sk": params.sortKeyBeginsWith,
    };
  }

  const result = await queryAll<T>(ddb, query, {
    limit: params.limit,
  });
  return result;
};

export const queryAllResourcesBySortKey = async <T = IDSK>(
  ddb: DocumentClient,
  params: {
    tableName: string;
    sk: string;
    idBeginsWith?: string;
    idEquals?: string;
    limit?: number;
  },
) => {
  const query: QueryCommandInput = {
    TableName: params.tableName,
    IndexName: "bySortKey",
    KeyConditionExpression: "#primary = :primary",
    ExpressionAttributeNames: {
      "#primary": "sk",
    },
    ExpressionAttributeValues: {
      ":primary": params.sk,
    },
  };

  if (params.idEquals) {
    query.KeyConditionExpression += ` AND #secondary = :secondary`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":secondary": params.idEquals,
    };
  } else if (params.idBeginsWith) {
    query.KeyConditionExpression += ` AND begins_with(#secondary, :secondary)`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":secondary": params.idBeginsWith,
    };
  }
  if (params.idEquals || params.idBeginsWith) {
    query.ExpressionAttributeNames = {
      ...query.ExpressionAttributeNames,
      "#secondary": "id",
    };
  }

  const result = await queryAll<T>(ddb, query, {
    limit: params.limit,
  });
  return result;
};

export const queryAllObjectivesBySortKey = async <T = IDSK>(
  ddb: DocumentClient,
  params: {
    tableName: string;
    sk: string;
    idBeginsWith?: string;
    idEquals?: string;
    limit?: number;
  },
) => {
  const query: QueryCommandInput = {
    TableName: params.tableName,
    IndexName: "BySortKey",
    KeyConditionExpression: "#primary = :primary",
    ExpressionAttributeNames: {
      "#primary": "sk",
    },
    ExpressionAttributeValues: {
      ":primary": params.sk,
    },
  };

  if (params.idEquals) {
    query.KeyConditionExpression += ` AND #secondary = :secondary`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":secondary": params.idEquals,
    };
  } else if (params.idBeginsWith) {
    query.KeyConditionExpression += ` AND begins_with(#secondary, :secondary)`;
    query.ExpressionAttributeValues = {
      ...query.ExpressionAttributeValues,
      ":secondary": params.idBeginsWith,
    };
  }
  if (params.idEquals || params.idBeginsWith) {
    query.ExpressionAttributeNames = {
      ...query.ExpressionAttributeNames,
      "#secondary": "id",
    };
  }

  const result = await queryAll<T>(ddb, query, {
    limit: params.limit,
  });
  return result;
};

export const queryAllObjectivesByAuthorityId = async <T = Objective>(
  ddb: DocumentClient,
  params: {
    tableName: string;
    authorityId: string;
    limit?: number;
  },
) => {
  const query: QueryCommandInput = {
    TableName: params.tableName,
    IndexName: "BySortKey",
    KeyConditionExpression: "#sk = :sk",
    FilterExpression: "begins_with(#scope, :scope)",
    ExpressionAttributeNames: {
      "#sk": "sk",
      "#scope": "scope",
    },
    ExpressionAttributeValues: {
      ":sk": "standard",
      ":scope": `${params.authorityId}#`,
    },
  };

  const result = await queryAll<T>(ddb, query, {
    limit: params.limit,
  });
  return result;
};

export const queryAllObjectivesByScope = async <T = Objective>(
  ddb: DocumentClient,
  params: {
    tableName: string;
    scope: string;
    limit?: number;
  },
) => {
  const query: QueryCommandInput = {
    TableName: params.tableName,
    IndexName: "objectivesByScopeSequence",
    KeyConditionExpression: "#scope = :scope",
    ExpressionAttributeNames: {
      "#scope": "scope",
    },
    ExpressionAttributeValues: {
      ":scope": params.scope,
    },
  };

  const result = await queryAll<T>(ddb, query, {
    limit: params.limit,
  });
  return result;
};

export const batchPutAll = async (
  ddb: DocumentClient,
  tableName: string,
  items: Record<string, any>[],
  options?: { concurrency?: number },
) => {
  await Bluebird.map(
    chunk(items, 25),
    (batch: any) =>
      ddb.batchWrite({
        RequestItems: {
          [tableName]: batch.map((item: any) => ({
            PutRequest: { Item: item },
          })),
        },
      }),
    { concurrency: options?.concurrency ?? 1 },
  );
};

export const batchDeleteAll = async (
  ddb: DocumentClient,
  tableName: string,
  items: Record<string, any>[],
  options?: { concurrency?: number },
) => {
  await Bluebird.map(
    chunk(items, 25),
    (batch: any) =>
      ddb.batchWrite({
        RequestItems: {
          [tableName]: batch.map((item: any) => ({
            DeleteRequest: { Key: item },
          })),
        },
      }),
    { concurrency: options?.concurrency ?? 25 },
  );
};

export const deleteItem = async (
  ddb: DocumentClient,
  TableName: string,
  Key: DocumentKey,
  options?: {
    dryrun?: boolean;
    verbose?: boolean;
  },
) => {
  if (options?.verbose) {
    console.log(
      `${options.dryrun ? "[DRYRUN] " : ""}DELETE`,
      stringifyItemKey(Key as IDSK),
    );
  }

  if (options?.dryrun) return;

  return ddb.delete({
    TableName,
    Key,
  });
};

/**
 * Convenience method for updating an item in DDB.
 *
 * Ex:
 * ```
 * await updateItemWithExpressions(
 *  ddb,
 *  env.getTableName("Resource"),
 *  { id: workspace.id, sk: "workspace" },
 *  {
 *    set: [["intercomId", intercomCompanyId]],
 *  }
 * );
 * ```
 */
export const updateItemWithExpressions = async (
  ddb: DocumentClient,
  TableName: string,
  Key: DocumentKey,
  expressions: {
    set: Array<[fieldName: string, fieldValue: any]>;
  },
) => {
  let UpdateExpression = "";
  const ExpressionAttributeNames: { [key: string]: string } = {};
  const ExpressionAttributeValues: { [key: string]: string } = {};

  if (expressions.set.length > 0) {
    UpdateExpression +=
      "SET " +
      expressions.set
        .map(([fieldName]) => `#${fieldName} = :${fieldName}`)
        .join(", ");

    for (const field of expressions.set) {
      ExpressionAttributeNames[`#${field[0]}`] = field[0];
      ExpressionAttributeValues[`:${field[0]}`] = field[1];
    }
  }

  const params: UpdateCommandInput = {
    TableName,
    Key,
    UpdateExpression,
    ExpressionAttributeNames,
    ExpressionAttributeValues,
    ConditionExpression: "attribute_exists(id)",
  };

  if (UpdateExpression.length === 0) {
    throw new Error("UpdateExpression is empty");
  }

  // console.log(params);
  await ddb.update(params);
};

/** Set isDeleted for resource to `true`. */
export const softDeleteResourceById = async (
  ddb: DocumentClient,
  params: {
    tableName: string;
    key: IDSK;
  },
) => {
  return ddb.update({
    TableName: params.tableName,
    Key: params.key,
    UpdateExpression: "SET isDeleted = :sk",
    ExpressionAttributeValues: {
      ":sk": true,
    },
  });
};

/** Set isDeleted for resource to `false`. */
export const restoreResourceById = async (
  ddb: DocumentClient,
  params: {
    tableName: string;
    key: IDSK;
  },
) => {
  return ddb.update({
    TableName: params.tableName,
    Key: params.key,
    UpdateExpression: "SET isDeleted = :sk",
    ExpressionAttributeValues: {
      ":sk": false,
    },
  });
};

/**
 * Gets the next assignable number (`idInc`) in a given workspace, or return the
 * resource's current idInc for saving new assets to DDB that would benefit from
 * having a number.
 */
export const getNumberForResource = async (
  env: AmplifyEnvironment,
  workspaceId: string,
  /** If provided, return the `idInc` of the existing item */
  existingKey?: IDSK,
) => {
  if (existingKey) {
    const existing = await getItem<Resource>(
      env.getDynamoDBDocumentClient(),
      env.getTableName("resource"),
      existingKey,
    );

    if (existing.Item?.idInc) return existing.Item.idInc;
  }

  return (await incrementWorkspaceCounter(
    env.getDynamoDBDocumentClient(),
    env.getTableName("resource"),
    workspaceId,
  )) as string;
};

export const getWorkspaceCounter = async (
  ddb: DocumentClient,
  TableName: string,
  workspaceId: string,
) => {
  const existing = await getItem<Workspace>(ddb, TableName, {
    id: workspaceId,
    sk: "workspace",
  });
  return existing.Item?.counter || 0;
};

export const incrementWorkspaceCounter = async (
  ddb: DocumentClient,
  TableName: string,
  workspaceId: string,
  amount: number = 1,
) => {
  const count = await ddb.update({
    TableName,
    Key: {
      id: workspaceId,
      sk: "workspace",
    },
    UpdateExpression: "ADD #counter :increment",
    ExpressionAttributeNames: {
      "#counter": "counter",
    },
    ExpressionAttributeValues: {
      ":increment": amount,
    },
    ReturnValues: "UPDATED_NEW",
  });
  return count.Attributes?.counter;
};

/** Set isDeleted for resource to `true`. */
export const setArchivedForResource = async (
  ddb: DocumentClient,
  params: {
    tableName: string;
    key: IDSK;
    isArchived?: boolean;
  },
) => {
  return ddb.update({
    TableName: params.tableName,
    Key: params.key,
    UpdateExpression: "SET #isArchived = :isArchived",
    ExpressionAttributeNames: {
      "#isArchived": "isArchived",
    },
    ExpressionAttributeValues: {
      ":isArchived": params.isArchived ?? false,
    },
  });
};

type BaseTypedItem<TIgnoreMissingWorkspaceId extends boolean> = AnyObject & {
  id: string;
  sk: string;
  schemaVersion: string;
  __typename?: string;
} & (TIgnoreMissingWorkspaceId extends false
    ? { workspaceId: string }
    : AnyObject);

export type PutTypedItemOptions<
  TIgnoreMissingWorkspaceId extends boolean = false,
> = {
  ignoreMissingWorkspaceId?: TIgnoreMissingWorkspaceId;
  createOnly?: boolean;
  allowCreate?: boolean;
  throwExceptionOnExisting?: boolean;
};

/**
 * Ensure the item being saved is properly typed and has required params.
 */
export async function putTypedItem<
  T extends BaseTypedItem<TIgnoreMissingWorkspaceId> = BaseTypedItem<false>,
  TIgnoreMissingWorkspaceId extends boolean = true,
>(params: {
  client: DocumentClient;
  putParams: Replace<PutCommandInput, { Item: T & { __typename: string } }>;
  options?: PutTypedItemOptions<TIgnoreMissingWorkspaceId>;
}) {
  const { client, putParams } = params;

  if (putParams.Item == null) throw new Error("Missing item");
  if (putParams.TableName == null) throw new Error("Missing table name");
  if (!putParams.Item.__typename) throw new Error("Missing __typename");
  if (!putParams.Item.id) throw new Error("Missing id");
  if (!putParams.Item.sk) throw new Error("Missing sk");
  if (!putParams.Item.schemaVersion) {
    throw new Error("Missing schemaVersion");
  }
  if (putParams.Item.schemaVersion !== "2020-01-01") {
    throw new Error("Invalid schemaVersion");
  }
  if (!params.options?.ignoreMissingWorkspaceId) {
    if (!putParams.Item.workspaceId) throw new Error("Missing workspaceId");
  }

  if (params.options?.createOnly) {
    const existing = await getItem(client, putParams.TableName, {
      id: putParams.Item.id,
      sk: putParams.Item.sk,
    });

    if (existing.Item) {
      if (params.options.throwExceptionOnExisting) {
        throw new Error(
          `Item ${putParams.Item.id}/${putParams.Item.sk} already exists.`,
        );
      }
      return;
    }
  }

  // console.log("Creating", {
  //   id: putParams.Item.id,
  //   sk: `app-license#${putParams.Item.workspaceId}`,
  //   workspaceId: putParams.Item.workspaceId,
  // });

  // console.log(
  //   "Putting item",
  //   putParams.Item.id,
  //   putParams.Item.sk,
  //   putParams.Item.__typename
  // );
  const tableName = putParams.TableName;
  const result = await putItem(client, tableName, putParams.Item, putParams);
  return {
    ...result,
    Item: putParams.Item,
  };
}

/**
 * Bulk query data using the Generator pattern without having to pull it all
 * into memory first.
 *
 * ```
 * for await (const item of queryAllResourcesByWorkspaceIdPaginated({
 *   ddbClient: ddb,
 *   query: {
 *     TableName: env.getTableName("Resource"),
 *     IndexName: "byWorkspaceId",
 *     KeyConditionExpression:
 *       "workspaceId = :workspaceId AND begins_with(sk, :sk)",
 *     ExpressionAttributeValues: {
 *       ":workspaceId": args["workspace-id"],
 *       ":sk": "comment",
 *     },
 *   },
 * })) {
 *   // do something with the row
 * }
 * ```
 */
export const queryPaginated = async function* <T = AnyObject>(params: {
  ddbClient: DocumentClient;
  query: QueryCommandInput;
  nextToken?: Record<string, AttributeValue>;
}) {
  let nextToken: Record<string, AttributeValue> | undefined = undefined;

  do {
    params.query.ExclusiveStartKey = nextToken;
    const result = await params.ddbClient.query(params.query);

    for (const item of result.Items ?? []) {
      yield item as T;
    }

    nextToken = result.LastEvaluatedKey ?? undefined;
  } while (nextToken != null);
};

export interface IteratorContext {
  page: number;
  processed: number;
  segment?: number;
  continue?: boolean;
}

/**
 * Performs a query or scan on a table and auto paginates until the end of the
 * table is reached, or when `context.continue` is set to `false` by its caller.
 * This method passes a context in the `callback` for access to page number, and
 * a flag to cancel pagination.
 */
export async function fullTableOperation<T = IDSK>(
  params: {
    client: DocumentClient;
    callback: (
      context: IteratorContext,
      data: T,
      segment: number | undefined,
    ) => Promise<void>;
    chunkSize?: number;
    iterationDelay?: number;
    context?: IteratorContext;
    index?: number;
  } & (
    | { operationFn: "scan"; operationParams: ScanCommandInput }
    | { operationFn: "query"; operationParams: QueryCommandInput }
  ),
) {
  if (!params.context) {
    params.context = {
      page: 0,
      processed: 0,
    };
  }

  if (!params.index) params.index = 0;

  params.context.page++;

  const segment =
    params.operationFn === "scan" ? params.operationParams.Segment : undefined;

  const result = await params.client[params.operationFn](
    params.operationParams,
  );

  if (result.Items) {
    const chunks = params.chunkSize
      ? chunk(result.Items, params.chunkSize)
      : [result.Items];

    for (const chunk of chunks) {
      if (params.context.continue === false) break;

      let index = 0;
      await Promise.all(
        chunk.map(async (item: any) => {
          if (!params.context) throw new Error("Missing Context");
          if (params.index === undefined) throw new Error("Missing Index");
          if (params.context.continue === false) return;
          params.context.processed++;
          index++;
          const result = await params.callback(params.context, item, segment);
          return result;
        }),
      );
      if (params.iterationDelay) {
        await delay(params.iterationDelay);
      }
      params.index = params.index + index;
    }
  }

  if (result?.LastEvaluatedKey && params.context.continue !== false) {
    params.operationParams.ExclusiveStartKey = result.LastEvaluatedKey;
    await fullTableOperation(params);
  }
}

export const scanTableParallel = async (params: {
  client: DocumentClient;
  tableName: string;
  totalSegments: number;
  callback: (
    context: IteratorContext,
    data: any,
    segment: number | undefined,
  ) => Promise<void>;
}) => {
  const { client, tableName, totalSegments, callback } = params;
  if (totalSegments > 5)
    throw new Error("TotalSegments cannot be greater than 5.");

  //
  // Initialize the context here so every segment shares the same instance
  //
  const context: IteratorContext = {
    page: 0,
    processed: 0,
  };

  await Promise.all(
    Array.from(Array(totalSegments).keys()).map(async (segment) => {
      await fullTableOperation({
        client: client,
        operationFn: "scan",
        operationParams: {
          TableName: tableName,
          Segment: segment,
          TotalSegments: totalSegments,
        },
        context,
        callback,
      });
    }),
  );
};

export const scanTableParallelForIndexing = async (params: {
  Client: DynamoDB;
  TableName: string;
  TotalSegments: number;
  ResultsCallback: (
    results: Array<Record<string, AttributeValue>>,
  ) => Promise<void>;
}) => {
  const { Client, TableName, TotalSegments } = params;

  const scan = async (
    Segment: number,
    TotalSegments: number,
    ExclusiveStartKey?: Record<string, AttributeValue>,
  ) => {
    const results = await Client.scan({
      TableName,
      Segment,
      TotalSegments,
      ExclusiveStartKey,
      Limit: 250,
    });

    if (!results.Items) return;
    await params.ResultsCallback(results.Items);

    if (results.LastEvaluatedKey) {
      await scan(Segment, TotalSegments, results.LastEvaluatedKey);
    }
  };

  await Promise.all(
    Array.from(Array(TotalSegments).keys()).map(
      async (segment) => await scan(segment, TotalSegments),
    ),
  );
};

/**
 * Scan or query a DDB table, continuously looping though all pages until the
 * scan is complete. Used for migration scripts. The `callback` is executed once
 * for every item found in the scan/query.
 *
 * Note: `scanTable` is suited for operating on each individual item, whereas this
 * method returns the entire chunk, which we needed for more efficient emergency
 * re-indexing options.
 */
export const scanTableChunk = async (
  params: {
    client: DocumentClient;
    tableName: string;
    indexName?: string;
    hashKeyField?: string;
    hashKeyValue?: string;
    sortKeyField?: string;
    sortKeyBeginsWith?: string;
    limit?: number;
  },
  callback: (item: any) => Promise<void>,
  lastEvaluatedKey?: { [key: string]: any },
) => {
  if (params.hashKeyField && params.hashKeyValue) {
    // console.log("query");
    let queryExpression = "#hk = :hkv";
    if (params.sortKeyBeginsWith) {
      queryExpression += " AND begins_with(#sk, :skv)";
    }

    const queryInput: QueryCommandInput = {
      TableName: params.tableName,
      Limit: params.limit ?? 50,
      ExclusiveStartKey: lastEvaluatedKey,
      KeyConditionExpression: queryExpression,
      ExpressionAttributeNames: {
        "#hk": params.hashKeyField,
      },
      ExpressionAttributeValues: {
        ":hkv": params.hashKeyValue,
      },
    };

    if (params.indexName) {
      queryInput.IndexName = params.indexName;
    }
    if (
      params.sortKeyField &&
      params.sortKeyBeginsWith &&
      queryInput.ExpressionAttributeNames &&
      queryInput.ExpressionAttributeValues
    ) {
      queryInput.ExpressionAttributeNames["#sk"] = params.sortKeyField;
      queryInput.ExpressionAttributeValues[":skv"] = params.sortKeyBeginsWith;
    }

    const result = await params.client.query(queryInput);

    if (result.Items) {
      await callback(result.Items);
    }

    if (result?.LastEvaluatedKey) {
      await scanTableChunk(params, callback, result.LastEvaluatedKey);
    }
  } else {
    /** scan */
    const result = await params.client.scan({
      TableName: params.tableName,
      ExclusiveStartKey: lastEvaluatedKey,
    });
    if (result.Items) {
      for (const item of result.Items) {
        callback(item);
      }
    }

    if (result?.LastEvaluatedKey) {
      await scanTableChunk(params, callback, result.LastEvaluatedKey);
    }
  }
};

/**
 * Scan or query a DDB table, continuously looping though all pages until the
 * scan is complete. Used for migration scripts. The `callback` is executed once
 * for every item found in the scan/query.
 */
export const scanTable = async (
  params: {
    client: DocumentClient;
    tableName: string;
    indexName?: string;
    hashKeyField?: string;
    hashKeyValue?: string;
    sortKeyField?: string;
    sortKeyValue?: string;
    sortKeyBeginsWith?: string;

    filterExpression?: string;
    filterExpressionAttributeNames?: { [key: string]: string };
    filterExpressionAttributeValues?: { [key: string]: any };
  },
  callback: (item: any, context: IteratorContext) => void,
  lastEvaluatedKey?: { [key: string]: any },
  previousContext?: IteratorContext,
) => {
  if (previousContext?.continue === false) return;

  const context = {
    page: (previousContext?.page ?? -1) + 1,
    processed: (previousContext?.processed ?? -1) + 1,
    continue: true,
  };

  if (params.hashKeyField && params.hashKeyValue) {
    // console.log("query");
    let queryExpression = "#hk = :hkv";
    if (params.sortKeyBeginsWith) {
      queryExpression += " AND begins_with(#sk, :skv)";
    }
    if (params.sortKeyValue) {
      queryExpression += " AND #sk = :skv";
    }

    const queryInput: QueryCommandInput = {
      TableName: params.tableName,
      ExclusiveStartKey: lastEvaluatedKey,
      KeyConditionExpression: queryExpression,
      FilterExpression: params.filterExpression,
      ExpressionAttributeNames: {
        "#hk": params.hashKeyField,
        ...params.filterExpressionAttributeNames,
      },
      ExpressionAttributeValues: {
        ":hkv": params.hashKeyValue,
        ...params.filterExpressionAttributeValues,
      },
    };

    if (params.indexName) {
      queryInput.IndexName = params.indexName;
    }
    if (
      params.sortKeyField &&
      (params.sortKeyBeginsWith || params.sortKeyValue) &&
      queryInput.ExpressionAttributeNames &&
      queryInput.ExpressionAttributeValues
    ) {
      queryInput.ExpressionAttributeNames["#sk"] = params.sortKeyField;
      queryInput.ExpressionAttributeValues[":skv"] =
        params.sortKeyBeginsWith ?? params.sortKeyValue;
    }

    const result = await params.client.query(queryInput);

    if (result.Items) {
      const chunks = chunk(result.Items, 500);
      for (const chunkItems of chunks) {
        await Promise.all(chunkItems.map((item) => callback(item, context)));
        await delay(500);
        if (!context.continue) break;
      }
    }

    if (result?.LastEvaluatedKey) {
      await scanTable(params, callback, result.LastEvaluatedKey, context);
    }
  } else {
    /** scan */
    const result = await params.client.scan({
      TableName: params.tableName,
      ExclusiveStartKey: lastEvaluatedKey,
    });
    if (result.Items) {
      for (const item of result.Items) {
        callback(item, context);
        if (!context.continue) break;
      }
    }

    if (result?.LastEvaluatedKey) {
      await delay(500);
      await scanTable(params, callback, result.LastEvaluatedKey, context);
    }
  }
};

/**
 * New adapter for initializing environment-specific objectes in the `api`
 * package. This type should have similar methods to the return type of
 * `initEnvironment` in the CLI package. Ultimately these objects need to come
 * together.
 */
export type AmplifyEnvironment = {
  getTableName: (
    string: "resource" | "Resource" | "objective" | "Objective" | string,
  ) => string;
  getDynamoDBClient: () => DynamoDB;
  getDynamoDBDocumentClient: () => DocumentClient;
  /** test out delete operations before actually deleting */
  dryrun?: boolean;
};

export const getAmplifyEnvironment = (params?: {
  ddbClient?: DynamoDB;
  ddbDocumentClient?: DocumentClient;
}): AmplifyEnvironment => {
  return {
    getTableName: (tableName: string) =>
      getTableNameInLambdaEnvironment(tableName),
    getDynamoDBDocumentClient: () =>
      params?.ddbDocumentClient ?? ddbSharedDocumentClient,
    getDynamoDBClient: () => params?.ddbClient ?? ddbSharedClient,
  };
};

export const putTags = async (
  env: AmplifyEnvironment,
  tableName: string,
  key: IDSK,
  tags: TagInput[],
) => {
  const ddb = env.getDynamoDBDocumentClient();

  // get existing item
  const item = await getItem(ddb, tableName, key);
  if (!item.Item) throw new Error("Item not found");

  if (!item.Item.tags) item.Item.tags = [];

  for (const tag of tags) {
    const tagKeyIndex = (item.Item.tags as Tag[]).findIndex(
      (t) => t.key === tags[0].key,
    );
    if (tagKeyIndex !== -1) {
      item.Item.tags[tagKeyIndex] = tag;
    } else {
      item.Item.tags.push(tag);
    }
  }

  await putItem(ddb, tableName, item.Item);
};

export const removeTags = async (
  env: AmplifyEnvironment,
  tableName: string,
  key: IDSK,
  // use callback to remove only tags that meet certain criteria
  callback: (tag: Tag) => boolean,
) => {
  const ddb = env.getDynamoDBDocumentClient();

  // get existing item
  const item = await getItem(ddb, tableName, key);
  if (!item.Item) throw new Error("Item not found");
  if (!item.Item.tags) return;

  item.Item.tags = (item.Item.tags as Tag[]).filter((tag) => !callback(tag));
  await putItem(ddb, tableName, item.Item);
};

export const keyEquals = (
  key: Record<string, any>,
  item: Record<string, any> | null | undefined,
) => {
  if (item == null) return false;
  for (const prop in key) {
    if (item[prop] !== key[prop]) return false;
  }
  return true;
};

/**
 * Technique to cause DDB -> ES trigger to fire again to delete an indexed
 * item. */
export const deleteIndexedItemUsingDdb = async (
  env: AmplifyEnvironment,
  item: AnyResource,
) => {
  await putItem(
    env.getDynamoDBDocumentClient(),
    env.getTableName("Resource"),
    item,
  );
  await delay(1000);
  await deleteItem(
    env.getDynamoDBDocumentClient(),
    env.getTableName("Resource"),
    {
      id: item.id,
      sk: item.sk,
    },
  );
};

export const queryAllResourceItemsById = async <T = IDSK>(
  env: AmplifyEnvironment,
  id: string,
) => {
  const allItemRecords = await queryAllResourcesById<T>(
    env.getDynamoDBDocumentClient(),
    {
      tableName: env.getTableName("Resource"),
      id: id,
    },
  );

  return allItemRecords;
};

export const resourceItemExists = async (
  env: AmplifyEnvironment,
  id: string,
  sk: string,
) => {
  const item = await getItem(
    env.getDynamoDBDocumentClient(),
    env.getTableName("resource"),
    {
      id,
      sk,
    },
  );
  return item.Item;
};

export const stringifyItemKey = (key: IDSK) => {
  return `${key.id}|${key.sk}`;
};
